// tydzien.ts — tydzień ISO 8601 dla wyboru okresu w Rozliczeniach (poniedziałek–piątek).
//
// Lustro `server/lib/rozliczenia/tydzienIso.js`: te same reguły i te same wektory testowe.
// Front liczy okres tylko po to, żeby od razu pokazać wybór; kwoty i okres w tabeli pochodzą
// z odpowiedzi serwera, który liczy je tym samym algorytmem.

const DZIEN_MS = 86_400_000;

export interface TydzienIso {
  rokIso: number;
  tydzien: number;
}

export interface OkresTygodnia extends TydzienIso {
  poniedzialek: string;
  piatek: string;
  dni: string[];
}

function dataUtc(rok: number, miesiac: number, dzien: number): Date {
  return new Date(Date.UTC(rok, miesiac - 1, dzien));
}

function dzienTygodniaIso(d: Date): number {
  return ((d.getUTCDay() + 6) % 7) + 1;
}

function poniedzialekTygodnia1(rokIso: number): Date {
  const czwartyStycznia = dataUtc(rokIso, 1, 4);
  return new Date(czwartyStycznia.getTime() - (dzienTygodniaIso(czwartyStycznia) - 1) * DZIEN_MS);
}

function zDaty(tekst: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(tekst);
  if (!m) throw new RangeError(`Niepoprawna data: ${tekst}`);
  const d = dataUtc(Number(m[1]), Number(m[2]), Number(m[3]));
  if (d.toISOString().slice(0, 10) !== tekst) throw new RangeError(`Niepoprawna data: ${tekst}`);
  return d;
}

const naDate = (d: Date): string => d.toISOString().slice(0, 10);

export function tydzienIso(data: string): TydzienIso {
  const d = zDaty(data);
  const czwartek = new Date(d.getTime() + (4 - dzienTygodniaIso(d)) * DZIEN_MS);
  const rokIso = czwartek.getUTCFullYear();
  const tydzien = 1 + Math.floor((czwartek.getTime() - poniedzialekTygodnia1(rokIso).getTime()) / (7 * DZIEN_MS));
  return { rokIso, tydzien };
}

export function liczbaTygodni(rokIso: number): number {
  return tydzienIso(naDate(dataUtc(rokIso, 12, 28))).tydzien;
}

export function okresTygodnia(rokIso: number, tydzien: number): OkresTygodnia {
  const poniedzialek = new Date(poniedzialekTygodnia1(rokIso).getTime() + (tydzien - 1) * 7 * DZIEN_MS);
  const dni = [0, 1, 2, 3, 4].map((i) => naDate(new Date(poniedzialek.getTime() + i * DZIEN_MS)));
  return { rokIso, tydzien, poniedzialek: dni[0] as string, piatek: dni[4] as string, dni };
}

/** Sąsiedni tydzień (−1 / +1) z przejściem przez granicę roku ISO. */
export function przesun(t: TydzienIso, oIle: number): TydzienIso {
  const poniedzialek = zDaty(okresTygodnia(t.rokIso, t.tydzien).poniedzialek);
  return tydzienIso(naDate(new Date(poniedzialek.getTime() + oIle * 7 * DZIEN_MS)));
}

/** Ostatni tydzień, którego piątek już nadszedł — w pon–czw poprzedni, w pt–nd bieżący. */
export function domyslnyTydzien(dzisiaj: string): TydzienIso {
  const d = zDaty(dzisiaj);
  const cofnij = dzienTygodniaIso(d) >= 5 ? 0 : 7;
  return tydzienIso(naDate(new Date(d.getTime() - cofnij * DZIEN_MS)));
}

/** „28.09 – 02.10.2026” (a przez granicę roku: „29.12.2025 – 02.01.2026”). */
export function opisOkresu(o: { poniedzialek: string; piatek: string }): string {
  const [rp, mp, dp] = o.poniedzialek.split('-');
  const [rk, mk, dk] = o.piatek.split('-');
  const poczatek = rp === rk ? `${dp}.${mp}` : `${dp}.${mp}.${rp}`;
  return `${poczatek} – ${dk}.${mk}.${rk}`;
}
